/**
 * PublicLayout — Shared layout for all public marketing pages of imparo360.it.
 * Includes a sticky navbar with responsive mobile menu and a footer.
 */

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Menu, X } from 'lucide-react';
import { clsx } from 'clsx';

const NAV_LINKS = [
  { label: 'Home', to: '/landing' },
  { label: 'Soluzioni', to: '/solutions' },
  { label: 'Security', to: '/security' },
  { label: 'Privacy', to: '/privacy' },
  { label: 'Terms', to: '/terms' },
];

const FOOTER_LINKS = [
  { label: 'Soluzioni', to: '/solutions' },
  { label: 'Security', to: '/security' },
  { label: 'Privacy', to: '/privacy' },
  { label: 'Terms', to: '/terms' },
];

export function PublicLayout({ children }: { children: ReactNode }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="flex min-h-screen flex-col bg-white">
      {/* ─────────────────────── Navbar ─────────────────────── */}
      <nav className="sticky top-0 z-50 border-b border-gray-200 bg-white shadow-sm">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <Link
            to="/landing"
            className="flex items-center gap-2 text-lg font-bold text-primary-950"
          >
            <GraduationCap className="h-7 w-7 text-primary-600" />
            <span>imparo360.it</span>
          </Link>

          {/* Desktop nav links */}
          <div className="hidden items-center gap-6 lg:flex">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-sm font-medium text-gray-700 transition-colors hover:text-primary-600"
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop "Accedi" button */}
          <div className="hidden lg:block">
            <Link
              to="/login"
              className="inline-flex items-center justify-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
            >
              Accedi
            </Link>
          </div>

          {/* Mobile hamburger toggle */}
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md p-2 text-gray-700 hover:bg-gray-100 hover:text-primary-600 lg:hidden"
            aria-label={mobileMenuOpen ? 'Chiudi menu' : 'Apri menu'}
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen((prev) => !prev)}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Mobile dropdown menu */}
        <div
          className={clsx(
            'overflow-hidden border-t border-gray-200 bg-white transition-all duration-200 lg:hidden',
            mobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0',
          )}
        >
          <div className="space-y-1 px-4 py-3 sm:px-6">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="block rounded-md px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-primary-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/login"
              className="mt-2 block rounded-lg bg-primary-600 px-4 py-2 text-center text-sm font-medium text-white transition-colors hover:bg-primary-700"
              onClick={() => setMobileMenuOpen(false)}
            >
              Accedi
            </Link>
          </div>
        </div>
      </nav>

      {/* ─────────────────────── Main content ─────────────────────── */}
      <main className="flex-1">{children}</main>

      {/* ─────────────────────── Footer ─────────────────────── */}
      <footer className="border-t border-gray-200 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start sm:justify-between">
            {/* Logo + tagline */}
            <div className="flex flex-col items-center gap-2 sm:items-start">
              <Link
                to="/landing"
                className="flex items-center gap-2 text-lg font-bold text-primary-950"
              >
                <GraduationCap className="h-6 w-6 text-primary-600" />
                <span>imparo360.it</span>
              </Link>
              <p className="text-sm text-gray-500">
                Piattaforma certificata per formazione professionale
              </p>
            </div>

            {/* Footer links */}
            <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
              {FOOTER_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="text-sm font-medium text-gray-600 transition-colors hover:text-primary-600"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Copyright */}
          <div className="mt-8 border-t border-gray-200 pt-6 text-center">
            <p className="text-sm text-gray-500">
              © 2025 imparo360.it
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}